import { Box, TextField } from '@mui/material';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { FormField } from '../../../../../form/FormField';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { FC, useEffect } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useDebounceEffect } from '../../../../../utils/useDebounceEffect';
import { z } from 'zod';
import { useTypedForm } from '../../../../../../../shared/fe/hooks/useForm';
import { DrawerWrapper } from '../../../../../drawer/DrawerWrapper';
import { useSnackbar } from '../../../../../store/snackbar';

const activityNameQuery = graphql(`
	query journeyDetail_getActivityName($activityId: UUID!) {
		getActivity(by: { id: $activityId }) {
			name
		}
	}
`);

const updateNameMutation = graphql(`
	mutation journeyDetail_updateName($id: UUID!, $name: String!) {
		updateActivity(by: { id: $id }, data: { name: $name }) {
			ok
			errorMessage
		}
	}
`);

export const nameEditDrawerSchema = z.object({
	activityId: z.string(),
});

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().min(1, intl.formatMessage({ id: 'form.edit-widget.name.error.empty-name' })),
	});

export const NameEditDrawer: FC<z.infer<typeof nameEditDrawerSchema>> = ({ activityId }) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const { data, isPending } = useApiQuery(activityNameQuery, {
		variables: { activityId },
	});

	const { mutate: updateActivityName } = useApiMutation(updateNameMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: { error: intl.formatMessage({ id: 'widget.name.update.error' }) },
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			}),
	});

	const form = useTypedForm({
		schema: createFormSchema(intl),
		mode: 'all',
		reValidateMode: 'onChange',
	});

	useEffect(() => {
		if (!isPending) {
			form.reset({ name: data?.getActivity?.name ?? '' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPending]);

	const { name } = form.watch();
	const { isValid } = form.formState;

	useDebounceEffect(() => {
		if (isValid) {
			updateActivityName({ id: activityId, name });
		}
	}, [name, isValid, activityId, updateActivityName]);

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'journey.detail.widget.name.title' })} />
			<Box width={'100%'}>
				<FormField
					form={form}
					name={'name'}
					label={intl.formatMessage({ id: 'drawer.edit-widget.label.name' })}
					render={(props) => <TextField {...props} />}
				/>
			</Box>
		</DrawerWrapper>
	);
};
